/**
 * Pure unit algebra. A {@link Unit} is a dimension vector — a normalized map from
 * base-dimension slug to integer exponent — so multiplication adds exponents,
 * division subtracts them, and cancellation falls out for free
 * (e.g. `oil/d ÷ liquid/d` → dimensionless). No conversion, no I/O, no mutation.
 */

/** A base dimension slug — the atomic unit the algebra tracks (e.g. `stb`, `d`, `psi`). */
export type BaseSlug = string;

/**
 * A unit as a dimension vector: base slug → integer exponent. Normalized so zero
 * exponents are absent; the empty map is {@link dimensionless}.
 */
export type Unit = ReadonlyMap<BaseSlug, number>;

/** The dimensionless unit (empty dimension vector). */
export const dimensionless: Unit = new Map();

/** Drop zero exponents so structurally-equal units share one canonical form. */
const normalize = (entries: Iterable<readonly [BaseSlug, number]>): Unit =>
  new Map([...entries].filter(([, exponent]) => exponent !== 0));

/** Combine two dimension vectors key-wise with {@link combine}, keeping every key from both. */
const mergeWith = (
  combine: (a: number, b: number) => number,
  left: Unit,
  right: Unit,
): readonly (readonly [BaseSlug, number])[] => {
  const slugs = new Set<BaseSlug>([...left.keys(), ...right.keys()]);
  return [...slugs].map((slug) => [slug, combine(left.get(slug) ?? 0, right.get(slug) ?? 0)] as const);
};

/** Build a normalized unit from base-slug/exponent pairs. */
export const unitOf = (entries: Iterable<readonly [BaseSlug, number]>): Unit => normalize(entries);

/** Multiply two units (add exponents). Commutative, associative, identity {@link dimensionless}. */
export const multiply = (a: Unit, b: Unit): Unit => normalize(mergeWith((x, y) => x + y, a, b));

/** The multiplicative reciprocal of a unit (negate exponents). */
export const reciprocal = (a: Unit): Unit => new Map([...a].map(([slug, exponent]) => [slug, -exponent]));

/** Divide two units (subtract exponents); `divide(a, a)` is {@link dimensionless}. */
export const divide = (a: Unit, b: Unit): Unit => multiply(a, reciprocal(b));

/** Raise a unit to an integer power (scale exponents); `power(a, 0)` is {@link dimensionless}. */
export const power = (a: Unit, n: number): Unit => normalize([...a].map(([slug, exponent]) => [slug, exponent * n]));

/** Structural equality of two normalized dimension vectors. */
export const equals = (a: Unit, b: Unit): boolean =>
  a.size === b.size && [...a].every(([slug, exponent]) => b.get(slug) === exponent);

/** Whether a unit carries no dimensions. */
export const isDimensionless = (u: Unit): boolean => u.size === 0;
